import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

const onLoaded = typeof onDocumentLoaded === 'function' ? onDocumentLoaded : callback => {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
  else callback();
};

class Header extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];
  #overflowWidth = null;
  #stickyObserver = null;
  #lastScrollY = 0;
  #frame = 0;
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
    if (this.#overflowWidth && window.innerWidth > this.#overflowWidth) this.#setOverflow(false);
  });

  #onOverflow = event => this.#setOverflow(Boolean(event.detail.minimumReached));

  #setOverflow(overflowing) {
    this.refs.headerDrawerContainer.classList.toggle('desktop:hidden', !overflowing);
    this.refs.headerMenu.classList.toggle('hidden', overflowing);
    this.#overflowWidth = overflowing ? window.innerWidth : null;
  }

  #scheduleScroll = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#updateScroll);
  };

  #updateScroll = () => {
    this.#frame = 0;
    const y = document.scrollingElement?.scrollTop ?? 0;
    this.dataset.scrollDirection = this.getBoundingClientRect().top >= 0
      ? 'none' : y < this.#lastScrollY ? 'up' : 'down';
    this.#lastScrollY = y;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#onOverflow);
    // Scroll-up visibility is owned by eido-header-behavior.js on every template.
    if (this.getAttribute('sticky') === 'always') {
      this.#lastScrollY = document.scrollingElement?.scrollTop ?? 0;
      this.#stickyObserver = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      }, { threshold: 1 });
      this.#stickyObserver.observe(this);
      document.addEventListener('scroll', this.#scheduleScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    this.removeEventListener('overflowMinimum', this.#onOverflow);
    document.removeEventListener('scroll', this.#scheduleScroll);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) customElements.define('header-component', Header);

onLoaded(() => {
  const header = document.querySelector('#header-component');
  const group = document.querySelector('#header-group');
  if (!group || !header) return;
  const observed = new WeakSet();
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      calculateHeaderGroupHeight(header, group);
    });
  };
  const resizeObserver = new ResizeObserver(schedule);
  const observeChildren = () => {
    for (const child of group.children) {
      if (child instanceof HTMLElement && !observed.has(child)) {
        observed.add(child);
        resizeObserver.observe(child);
      }
    }
  };
  observeChildren();
  resizeObserver.observe(group);
  const mutations = new MutationObserver(observeChildren);
  mutations.observe(group, { childList: true });
  const cleanup = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    resizeObserver.disconnect();
    mutations.disconnect();
  };
  window.addEventListener('pagehide', cleanup, { once: true });
  window.addEventListener('beforeunload', cleanup, { once: true });
});
